/**
 * web/assets.ts
 * Vite-native asset loader for the Canvas2D renderer.
 *
 * Uses Vite's static asset imports (`import url from '../src/assets/.../foo.png'`)
 * to resolve PNG / Tiled-JSON URLs at build time, then preloads `HTMLImageElement`s
 * and hands a fully-loaded `RenderAssets` to `setRenderAssets`.
 *
 * @module web/assets
 */

/// <reference lib="dom" />

import { setRenderAssets, setActiveTiledMap, setActiveWorldId } from '@src/game/rendering/renderAssetStore';
import { loadAllImages } from '@src/game/rendering/imageLoader';
import type { TiledMap } from '@src/game/rendering/TileMapTypes';
import type { WorldId } from '@src/constants/gameConstants';
import Logger from '@src/game/utils/logger';

import novaUrl from '../src/assets/sprites/nova-v2-sheet.png';
import avonUrl from '../src/assets/sprites/avon-sheet.png';
import belleUrl from '../src/assets/sprites/belle-sheet.png';
import tilesetUrl from '../src/assets/tilesets/world-1-sunmeadow.png';
import world3TilesetUrl from '../src/assets/tilesets/world-3-brasscloud.png';
import world4TilesetUrl from '../src/assets/tilesets/world-4-crystaltide.png';
import world5TilesetUrl from '../src/assets/tilesets/world-5-eclipsebastion.png';
import bgFarUrl from '../src/assets/sprites/bg-world1-far-stripped.png';
import bgMidUrl from '../src/assets/sprites/bg-world1-mid-stripped.png';
import bgNearUrl from '../src/assets/sprites/bg-world1-near.png';
import bgWorld3FarUrl from '../src/assets/sprites/bg-world3-far.png';
import bgWorld3MidUrl from '../src/assets/sprites/bg-world3-mid.png';
import bgWorld3NearUrl from '../src/assets/sprites/bg-world3-near.png';
import bgWorld4FarUrl from '../src/assets/sprites/bg-world4-far.png';
import bgWorld4MidUrl from '../src/assets/sprites/bg-world4-mid.png';
import bgWorld4NearUrl from '../src/assets/sprites/bg-world4-near.png';
import bgWorld5FarUrl from '../src/assets/sprites/bg-world5-far.png';
import bgWorld5MidUrl from '../src/assets/sprites/bg-world5-mid.png';
import bgWorld5NearUrl from '../src/assets/sprites/bg-world5-near.png';
import nibblerUrl from '../src/assets/sprites/nibbler-sheet.png';
import thornhopUrl from '../src/assets/sprites/thornhop-sheet.png';
import gloomBatUrl from '../src/assets/sprites/gloombat-sheet.png';
import novaShipUrl from '../src/assets/sprites/nova_ship.png';
import flameIdleUrl from '../src/assets/sprites/flame_idle.png';
import flameCruiseUrl from '../src/assets/sprites/flame_cruise.png';
import flameBoostUrl from '../src/assets/sprites/flame_boost.png';
import laserHitUrl from '../src/assets/sprites/laser-hit-sheet.png';
import heartUrl from '../src/assets/sprites/heart.png';
import starSeedUrl from '../src/assets/sprites/star-seed.png';
import shardUrl from '../src/assets/sprites/constellation-shard.png';
import checkpointActiveUrl from '../src/assets/sprites/checkpoint-active.png';
import checkpointInactiveUrl from '../src/assets/sprites/checkpoint-inactive.png';
import laserPulseUrl from '../src/assets/sprites/laser-pulse.png';
import cloudLargeUrl from '../src/assets/sprites/cloud-large.png';
import cloudMediumUrl from '../src/assets/sprites/cloud-medium.png';
import shipHealthyUrl from '../src/assets/sprites/ship-healthy.png';
import shipOnFireUrl from '../src/assets/sprites/ship-on-fire.png';
import asteroidXLUrl from '../src/assets/sprites/asteroid-xl.png';
import asteroidLGUrl from '../src/assets/sprites/asteroid-lg.png';
import asteroidMDUrl from '../src/assets/sprites/asteroid-md.png';
import asteroidSMUrl from '../src/assets/sprites/asteroid-sm.png';
import portalActiveUrl from '../src/assets/sprites/portal-active.png';
import portalInactiveUrl from '../src/assets/sprites/portal-inactive.png';
import portalPedestalUrl from '../src/assets/sprites/portal-pedestal.png';
import weaponPickupGemShardUrl from '../src/assets/sprites/weapon-pickup-gem-shard.png';
import weaponPickupBoomerangUrl from '../src/assets/sprites/weapon-pickup-boomerang.png';
import weaponPickupFireOrbUrl from '../src/assets/sprites/weapon-pickup-fire-orb.png';
import weaponPickupIceCrystalUrl from '../src/assets/sprites/weapon-pickup-ice-crystal.png';

import level11 from '../src/assets/levels/world-1/level-1-1.json';
import level12 from '../src/assets/levels/world-1/level-1-2.json';
import level13 from '../src/assets/levels/world-1/level-1-3.json';
import level14 from '../src/assets/levels/world-1/level-1-4.json';
import level1s from '../src/assets/levels/world-1/level-1-s.json';
import level31 from '../src/assets/levels/world-3/level-3-1.json';
import level32 from '../src/assets/levels/world-3/level-3-2.json';
import level33 from '../src/assets/levels/world-3/level-3-3.json';
import level34 from '../src/assets/levels/world-3/level-3-4.json';
import level3s from '../src/assets/levels/world-3/level-3-S.json';
import level3B from '../src/assets/levels/world-3/level-3-B.json';

/**
 * Static cross-world tile map registry. Used by the screen layer to set
 * the active map for any platformer level prior to navigating into the
 * Canvas2D scene. Space-shmup (W2) levels are routed through the
 * dedicated space pipeline and intentionally absent here.
 */
export const WORLD_TILED_MAPS: Record<string, TiledMap> = {
  '1-1': level11 as TiledMap,
  '1-2': level12 as TiledMap,
  '1-3': level13 as TiledMap,
  '1-4': level14 as TiledMap,
  '1-0': level1s as TiledMap,
  '3-1': level31 as TiledMap,
  '3-2': level32 as TiledMap,
  '3-3': level33 as TiledMap,
  '3-4': level34 as TiledMap,
  '3-0': level3s as TiledMap,
  '3-B': level3B as TiledMap,
};

const SPRITE_URLS = [
  novaUrl, avonUrl, belleUrl, tilesetUrl, world3TilesetUrl, world4TilesetUrl, world5TilesetUrl, bgFarUrl, bgMidUrl, bgNearUrl,
  bgWorld3FarUrl, bgWorld3MidUrl, bgWorld3NearUrl,
  bgWorld4FarUrl, bgWorld4MidUrl, bgWorld4NearUrl,
  bgWorld5FarUrl, bgWorld5MidUrl, bgWorld5NearUrl,
  nibblerUrl, thornhopUrl, gloomBatUrl, novaShipUrl, flameIdleUrl,
  flameCruiseUrl, flameBoostUrl, laserHitUrl, heartUrl, starSeedUrl,
  shardUrl, checkpointActiveUrl, checkpointInactiveUrl, laserPulseUrl,
  cloudLargeUrl, cloudMediumUrl, shipHealthyUrl, shipOnFireUrl,
  asteroidXLUrl, asteroidLGUrl, asteroidMDUrl, asteroidSMUrl,
  portalActiveUrl, portalInactiveUrl, portalPedestalUrl,
  weaponPickupGemShardUrl, weaponPickupBoomerangUrl,
  weaponPickupFireOrbUrl, weaponPickupIceCrystalUrl,
] as const;

/** Module-level guard so repeated screen mounts don't re-fetch every image. */
let _loaded = false;
let _loadPromise: Promise<void> | null = null;

/**
 * Push the given tile map to the Canvas2D renderer immediately.
 * Use before mounting a game screen so the canvas paints the correct level
 * even if {@link loadGameAssets} is still in flight.
 */
export function setTiledMapForLevel(levelId: string): void {
  const map = WORLD_TILED_MAPS[levelId];
  if (map) setActiveTiledMap(map);
  // DECISION: World id is the prefix before the first '-' in the level key
  // ('1-1', '2-S', '3-1', ...). Parse it once at scene-entry time so the
  // renderer can pick per-world art (parallax, etc.) without re-deriving
  // the world from the tiled map every frame.
  const prefix = levelId.split('-')[0];
  const parsed = Number(prefix);
  if (parsed === 1 || parsed === 2 || parsed === 3 || parsed === 4 || parsed === 5) {
    setActiveWorldId(parsed as WorldId);
  }
}

/**
 * Load all sprite assets and hand them to the Canvas2D renderer.
 * Idempotent — subsequent calls return the same in-flight or settled
 * promise so navigating between screens doesn't re-issue 30+ requests.
 *
 * @param activeTiledMap Tile map to bundle into RenderAssets (defaults to
 *   1-1 when omitted).
 */
export function loadGameAssets(activeTiledMap: TiledMap = level11 as TiledMap): Promise<void> {
  // DECISION: Push the active map to the renderer on every call — including
  // while the initial image batch is still in flight. Without this, a
  // late-arriving level change made during the first load gets discarded
  // and the renderer stays stuck on the first call's map.
  setActiveTiledMap(activeTiledMap);
  if (_loaded) return Promise.resolve();
  if (_loadPromise) return _loadPromise;

  _loadPromise = loadAllImages([...SPRITE_URLS]).then((results) => {
    const allOk = results.every((r) => r.ok);
    if (!allOk) {
      Logger.warn('web/assets', 'One or more images failed to load — staying on greybox');
      return;
    }
    const [
      nova, avon, belle, tileset, tilesetW3, tilesetW4, tilesetW5, bgFar, bgMid, bgNear,
      bgW3Far, bgW3Mid, bgW3Near,
      bgW4Far, bgW4Mid, bgW4Near,
      bgW5Far, bgW5Mid, bgW5Near,
      nibbler, thornhop, gloomBat, novaShip, flameIdle,
      flameCruise, flameBoost, laserHit, heart, starSeed,
      shard, cpActive, cpInactive, laserPulse,
      cloudLg, cloudMd, shipHealthy, shipOnFire,
      asteroidXL, asteroidLG, asteroidMD, asteroidSM,
      portalActive, portalInactive, portalPedestal,
      wpGem, wpBoomerang, wpFireOrb, wpIceCrystal,
    ] = results;
    if (!nova || !avon || !belle || !tileset || !tilesetW3 || !tilesetW4 || !tilesetW5 || !bgFar || !bgMid || !bgNear ||
        !bgW3Far || !bgW3Mid || !bgW3Near ||
        !bgW4Far || !bgW4Mid || !bgW4Near ||
        !bgW5Far || !bgW5Mid || !bgW5Near ||
        !nibbler || !thornhop || !gloomBat || !novaShip || !flameIdle ||
        !flameCruise || !flameBoost || !laserHit || !heart || !starSeed ||
        !shard || !cpActive || !cpInactive || !laserPulse ||
        !cloudLg || !cloudMd || !shipHealthy || !shipOnFire ||
        !asteroidXL || !asteroidLG || !asteroidMD || !asteroidSM ||
        !portalActive || !portalInactive || !portalPedestal ||
        !wpGem || !wpBoomerang || !wpFireOrb || !wpIceCrystal) {
      Logger.warn('web/assets', 'Sprite load returned unexpected length');
      return;
    }
    setRenderAssets({
      novaImg: nova.img,
      avonImg: avon.img,
      belleImg: belle.img,
      tilesetImg: tileset.img,
      world3TilesetImg: tilesetW3.img,
      world4TilesetImg: tilesetW4.img,
      world5TilesetImg: tilesetW5.img,
      bgFarImg: bgFar.img,
      bgMidImg: bgMid.img,
      bgNearImg: bgNear.img,
      world3BgFarImg: bgW3Far.img,
      world3BgMidImg: bgW3Mid.img,
      world3BgNearImg: bgW3Near.img,
      world4BgFarImg: bgW4Far.img,
      world4BgMidImg: bgW4Mid.img,
      world4BgNearImg: bgW4Near.img,
      world5BgFarImg: bgW5Far.img,
      world5BgMidImg: bgW5Mid.img,
      world5BgNearImg: bgW5Near.img,
      nibblerImg: nibbler.img,
      thornhopImg: thornhop.img,
      gloomBatImg: gloomBat.img,
      novaShipImg: novaShip.img,
      flameIdleImg: flameIdle.img,
      flameCruiseImg: flameCruise.img,
      flameBoostImg: flameBoost.img,
      laserHitImg: laserHit.img,
      heartImg: heart.img,
      starSeedImg: starSeed.img,
      shardImg: shard.img,
      checkpointActiveImg: cpActive.img,
      checkpointInactiveImg: cpInactive.img,
      laserPulseImg: laserPulse.img,
      cloudLargeImg: cloudLg.img,
      cloudMediumImg: cloudMd.img,
      shipHealthyImg: shipHealthy.img,
      shipOnFireImg: shipOnFire.img,
      asteroidXLImg: asteroidXL.img,
      asteroidLGImg: asteroidLG.img,
      asteroidMDImg: asteroidMD.img,
      asteroidSMImg: asteroidSM.img,
      portalActiveImg: portalActive.img,
      portalInactiveImg: portalInactive.img,
      portalPedestalImg: portalPedestal.img,
      weaponPickupGemShardImg: wpGem.img,
      weaponPickupBoomerangImg: wpBoomerang.img,
      weaponPickupFireOrbImg: wpFireOrb.img,
      weaponPickupIceCrystalImg: wpIceCrystal.img,
      tiledMap: activeTiledMap,
    });
    _loaded = true;
  });
  return _loadPromise;
}

/** Test-only hook so unit tests can reset the load guard between runs. */
export function _resetAssetsForTesting(): void {
  _loaded = false;
  _loadPromise = null;
}
